import React, { useState } from 'react';
import { Quotation, AccommodationInfo } from './types';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult
} from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';
import { 
  Building, 
  GripVertical, 
  PlusCircle, 
  X,
  MoreHorizontal,
  ArrowUp,
  ArrowDown,
  Pencil,
  Trash,
  Settings,
  CircleDot
} from 'lucide-react';
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { cn } from '@/lib/utils';

// Custom styled section title with orange underline and icon
const StyledSectionTitle = ({ 
  children, 
  className, 
  icon: Icon = CircleDot,
  ...props 
}: React.HTMLAttributes<HTMLHeadingElement> & { icon?: React.ElementType }) => (
  <h3 
    className={cn("border-b border-voah-coral pb-1 inline-flex items-center gap-1.5 text-left text-base font-medium text-gray-700 mb-3", className)}
    {...props}
  >
    <Icon className="h-4 w-4 text-voah-coral flex-shrink-0" />
    <span className="whitespace-nowrap">{children}</span>
  </h3>
);

// New interface for custom accommodation groups
export interface AccommodationGroup {
  id: string;
  name: string;
  accommodationIds: string[];
  order: number;
  description?: string; // Descrição do grupo
}

// Extended Quotation interface
export interface ExtendedQuotation extends Quotation {
  accommodationGroups?: AccommodationGroup[];
  useCustomAccommodationGroups?: boolean;
}

// Add an extended version of AccommodationInfo with a page property
interface ExtendedAccommodationInfo extends AccommodationInfo {
  page?: number; // Add this property to our local interface
}

interface AccommodationsTabProps {
  quotation: ExtendedQuotation;
  handleChange: (field: keyof ExtendedQuotation, value: any) => void;
  isEditing?: boolean;
}

export function QuotationAccommodationsTab({ quotation, handleChange, isEditing = false }: AccommodationsTabProps) {
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  
  const useCustomGroups = quotation.useCustomAccommodationGroups || false;
  const groups = quotation.accommodationGroups || [];
  
  // Get all accommodations from all pages
  const allAccommodations = quotation.pages.flatMap(page => 
    (page.accommodations || []).map(acc => ({
      ...acc,
      page: page.globalOrder || Number(page.id.replace(/\D/g, '')) || 1 // Derive page number from page globalOrder or ID
    })) as ExtendedAccommodationInfo[]
  );

  // Handler for toggling map section visibility
  const handleToggleMapSectionVisibility = (checked: boolean) => {
    handleChange('showAccommodationsMapSection', checked);
  };

  // Handler for toggling custom groups
  const handleToggleCustomGroups = (checked: boolean) => {
    handleChange('useCustomAccommodationGroups', checked);
    
    // If enabling custom groups for the first time, initialize with default groups by page
    if (checked && (!quotation.accommodationGroups || quotation.accommodationGroups.length === 0)) {
      // Group accommodations by page number
      const pageGroups: Record<string, string[]> = {}; // Use string keys for safety
      
      // Make sure we're working with accommodations that have page property
      allAccommodations.forEach((acc: ExtendedAccommodationInfo) => {
        const pageNumber = String(acc.page || 1); // Convert to string for safe indexing
        if (!pageGroups[pageNumber]) {
          pageGroups[pageNumber] = [];
        }
        pageGroups[pageNumber].push(acc.id);
      });
      
      // Create initial groups based on pages
      const initialGroups: AccommodationGroup[] = Object.entries(pageGroups).map(([page, ids], index) => ({
        id: uuidv4(),
        name: `Página ${page}`,
        order: index,
        accommodationIds: ids
      }));
      
      handleChange('accommodationGroups', initialGroups);
    }
  };

  // Handler for adding a new group
  const handleAddGroup = () => {
    const newGroups = [...groups, {
      id: uuidv4(),
      name: `Novo Grupo ${groups.length + 1}`,
      order: groups.length,
      accommodationIds: []
    }];
    
    handleChange('accommodationGroups', newGroups);
  };

  // Handler for editing group name
  const handleEditGroupName = (groupId: string, currentName: string, currentDescription?: string) => {
    setEditingGroupId(groupId);
    setNewGroupName(currentName);
    setNewGroupDescription(currentDescription || '');
  };

  // Handler for saving edited group name
  const handleSaveGroupName = (groupId: string) => {
    const updatedGroups = groups.map(g => 
      g.id === groupId ? { ...g, name: newGroupName, description: newGroupDescription } : g
    );
    
    handleChange('accommodationGroups', updatedGroups);
    setEditingGroupId(null);
    setNewGroupName('');
    setNewGroupDescription('');
  };

  // Handler for removing a group
  const handleRemoveGroup = (groupId: string) => {
    const updatedGroups = groups
      .filter(g => g.id !== groupId)
      .map((g, index) => ({ ...g, order: index }));
    
    handleChange('accommodationGroups', updatedGroups);
  };

  // Handler for moving a group up or down
  const handleMoveGroup = (groupId: string, direction: 'up' | 'down') => {
    if (!quotation.accommodationGroups) return;
    
    const currentIndex = quotation.accommodationGroups.findIndex(g => g.id === groupId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' 
      ? Math.max(0, currentIndex - 1) 
      : Math.min(quotation.accommodationGroups.length - 1, currentIndex + 1);
    
    if (newIndex === currentIndex) return;
    
    const updatedGroups = [...quotation.accommodationGroups];
    const [movedGroup] = updatedGroups.splice(currentIndex, 1);
    updatedGroups.splice(newIndex, 0, movedGroup);
    
    // Update order property
    const groupsWithUpdatedOrder = updatedGroups.map((group, index) => ({
      ...group,
      order: index
    }));
    
    handleChange('accommodationGroups', groupsWithUpdatedOrder);
  };

  // Handler for adding accommodation to a group
  const handleAddAccommodationToGroup = (groupId: string, accommodationId: string) => {
    if (!quotation.accommodationGroups) return;
    
    const updatedGroups = quotation.accommodationGroups.map(group => {
      if (group.id === groupId) {
        // Only add if not already in group
        if (!group.accommodationIds.includes(accommodationId)) {
          return {
            ...group,
            accommodationIds: [...group.accommodationIds, accommodationId]
          };
        }
      }
      return group;
    });
    
    handleChange('accommodationGroups', updatedGroups);
  };

  // Handler for removing accommodation from a group
  const handleRemoveAccommodationFromGroup = (groupId: string, accommodationId: string) => {
    if (!quotation.accommodationGroups) return;
    
    const updatedGroups = quotation.accommodationGroups.map(group => {
      if (group.id === groupId) {
        return {
          ...group,
          accommodationIds: group.accommodationIds.filter(id => id !== accommodationId)
        };
      }
      return group;
    });
    
    handleChange('accommodationGroups', updatedGroups);
  };

  // Handler for drag and drop
  const handleDragEnd = (result: DropResult) => {
    const { source, destination, type } = result;
    
    console.log('Drag end result:', {
      source,
      destination,
      type,
      draggableId: result.draggableId
    });
    
    // Dropped outside the list
    if (!destination) {
      console.log('Dropped outside list, ignoring');
      return;
    }
    
    // No change needed
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      console.log('No position change, ignoring');
      return;
    }
    
    let newGroups = [...groups];
    
    // Reordering groups
    if (type === 'GROUP') {
      console.log('Reordering groups');
      const [removed] = newGroups.splice(source.index, 1);
      newGroups.splice(destination.index, 0, removed);
      
      // Update order property
      newGroups = newGroups.map((g, index) => ({
        ...g,
        order: index
      }));
      
      handleChange('accommodationGroups', newGroups);
      return;
    }
    
    // Special case: dragging from unassigned to a group
    if (source.droppableId === 'unassigned' && type === 'ACCOMMODATION') {
      console.log('Moving from unassigned to group:', destination.droppableId);
      
      // Find the destination group
      const destGroup = newGroups.find(g => g.id === destination.droppableId);
      if (!destGroup) {
        console.log('Destination group not found');
        return;
      }
      
      // Get the unassigned accommodations
      const unassignedAccommodations = allAccommodations.filter(
        acc => !groups.some(g => g.accommodationIds.includes(acc.id))
      );
      
      // Get the dragged accommodation ID
      const draggedAccId = unassignedAccommodations[source.index].id;
      console.log('Adding accommodation to group:', draggedAccId);
      
      // Add to destination group at the correct position
      const newDestIds = [...destGroup.accommodationIds];
      newDestIds.splice(destination.index, 0, draggedAccId);
      
      // Update groups
      const updatedGroups = newGroups.map(g => 
        g.id === destination.droppableId 
          ? { ...g, accommodationIds: newDestIds }
          : g
      );
      
      handleChange('accommodationGroups', updatedGroups);
      return;
    }
    
    // Reordering accommodations within a group
    if (source.droppableId === destination.droppableId) {
      console.log('Reordering accommodations within group:', source.droppableId);
      const group = newGroups.find(g => g.id === source.droppableId);
      if (!group) {
        console.log('Group not found:', source.droppableId);
        return;
      }
      
      const newAccommodationIds = [...group.accommodationIds];
      const [removed] = newAccommodationIds.splice(source.index, 1);
      newAccommodationIds.splice(destination.index, 0, removed);
      
      console.log('New accommodation order:', newAccommodationIds);
      
      const updatedGroups = newGroups.map(g => 
        g.id === source.droppableId 
          ? { ...g, accommodationIds: newAccommodationIds }
          : g
      );
      
      handleChange('accommodationGroups', updatedGroups);
      return;
    }
    
    // Moving accommodation between groups
    console.log('Moving accommodation between groups:', {
      sourceGroup: source.droppableId,
      destGroup: destination.droppableId
    });
    
    const sourceGroup = newGroups.find(g => g.id === source.droppableId);
    const destGroup = newGroups.find(g => g.id === destination.droppableId);
    
    if (!sourceGroup || !destGroup) {
      console.log('Source or destination group not found:', {
        sourceFound: !!sourceGroup,
        destFound: !!destGroup
      });
      return;
    }
    
    const newSourceIds = [...sourceGroup.accommodationIds];
    const newDestIds = [...destGroup.accommodationIds];
    
    const [movedAccommodation] = newSourceIds.splice(source.index, 1);
    newDestIds.splice(destination.index, 0, movedAccommodation);
    
    console.log('Moving accommodation:', {
      id: movedAccommodation,
      fromGroup: sourceGroup.name,
      toGroup: destGroup.name
    });
    
    const updatedGroups = newGroups.map(g => {
      if (g.id === source.droppableId) {
        return { ...g, accommodationIds: newSourceIds };
      }
      if (g.id === destination.droppableId) {
        return { ...g, accommodationIds: newDestIds };
      }
      return g;
    });
    
    handleChange('accommodationGroups', updatedGroups);
  };

  // Get accommodation by ID
  const getAccommodationById = (id: string): ExtendedAccommodationInfo | undefined => {
    return allAccommodations.find(acc => acc.id === id);
  };

  // Function to get page name for an accommodation
  const getPageNameForAccommodation = (accommodationId: string): string => {
    for (const page of quotation.pages) {
      const found = page.accommodations?.find(acc => acc.id === accommodationId);
      if (found) {
        return page.title || `Página ${page.id}`;
      }
    }
    return "Página não encontrada";
  };

  // Sort groups by order
  const sortedGroups = [...(quotation.accommodationGroups || [])].sort((a, b) => a.order - b.order);

  // Render the unassigned accommodations section
  const renderUnassignedAccommodations = () => {
    const unassigned = allAccommodations.filter(acc => !groups.some(g => g.accommodationIds.includes(acc.id)));
    
    if (unassigned.length === 0) return null;
    
    return (
      <Card className="mb-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Acomodações Não Atribuídas</CardTitle>
        </CardHeader>
        <CardContent>
          <Droppable droppableId="unassigned" type="ACCOMMODATION">
            {(provided) => (
              <div 
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="space-y-2"
              >
                {unassigned.map((acc, index) => (
                  <Draggable
                    key={acc.id}
                    draggableId={acc.id}
                    index={index}
                    isDragDisabled={!isEditing}
                  >
                    {(provided) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className="flex items-center justify-between bg-muted rounded-md p-2"
                      >
                        <div className="flex items-center gap-2">
                          <GripVertical className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">{acc.name} (Página {acc.page || 1})</span>
                        </div>
                        <div className="flex space-x-2">
                          {groups.map(group => (
                            <Button 
                              key={group.id}
                              variant="ghost" 
                              size="sm"
                              className="h-7 px-2"
                              onClick={() => handleAddAccommodationToGroup(group.id, acc.id)}
                              title={`Adicionar ao grupo: ${group.name}`}
                            >
                              <PlusCircle className="h-4 w-4" />
                              <span className="ml-1 text-xs">{group.name}</span>
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col space-y-1.5">
        <h3 className="text-lg font-semibold text-gray-800">Configuração de Hospedagens</h3>
        <p className="text-sm text-gray-600">Personalize como as hospedagens são agrupadas e exibidas para o cliente.</p>
      </div>
      
      <Card>
        <CardHeader>
          <CardTitle>Descrição de Hospedagens</CardTitle>
          <CardDescription>
            Esta descrição será exibida no início da aba de Hospedagens para o cliente.
            Você pode adicionar observações gerais, instruções especiais ou detalhes importantes sobre as hospedagens.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <RichTextEditor
            value={quotation.accommodationsDescription || ''}
            onChange={(value) => handleChange('accommodationsDescription', value)}
            readOnly={!isEditing}
            placeholder="Escreva uma descrição sobre as hospedagens selecionadas..."
            rows={6}
          />
          {isEditing && (
            <div className="mt-2 text-xs text-muted-foreground">
              Dica: Você pode adicionar formatação ao texto, incluir links, listas e outros elementos para melhorar a apresentação das informações ao cliente.
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardContent className="pt-6">
          <StyledSectionTitle className="mb-4" icon={Settings}>
            Configurações
          </StyledSectionTitle>
          
          {/* Toggle para visibilidade da seção de mapa */}
          <div className="flex items-center mb-3">
            <label className="text-sm font-medium w-[30%] pr-4 text-right">Exibição da Seção</label>
            <div className="flex items-center gap-2 w-[70%]">
              <Switch 
                id="show-accommodations-map-section"
                checked={quotation.showAccommodationsMapSection !== false} 
                onCheckedChange={handleToggleMapSectionVisibility}
              />
              <Label htmlFor="show-accommodations-map-section" className="text-sm font-medium">
                Mostrar lista navegável e mapa
              </Label>
              <span className="text-xs text-gray-500 ml-3">
                Exibe a seção inicial com lista de hospedagens e mapa interativo.
              </span>
            </div>
          </div>
          
          {/* Toggle para grupos personalizados */}
          <div className="flex items-center mb-3">
            <label className="text-sm font-medium w-[30%] pr-4 text-right">Grupos de Acomodações</label>
            <div className="flex items-center gap-2 w-[70%]">
              <Switch 
                id="use-custom-groups"
                checked={useCustomGroups} 
                onCheckedChange={handleToggleCustomGroups}
              />
              <Label htmlFor="use-custom-groups" className="text-sm font-medium">
                Usar grupos personalizados
              </Label>
              <span className="text-xs text-gray-500 ml-3">
                {useCustomGroups 
                  ? "Defina agrupamentos personalizados para as acomodações." 
                  : "As acomodações serão agrupadas por página."}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Agrupamento de Hospedagens</CardTitle>
          <CardDescription>
            Escolha como as hospedagens serão agrupadas e exibidas na visualização do cliente
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="text-sm text-gray-500">
              {useCustomGroups 
                ? "As hospedagens serão exibidas nos grupos personalizados que você definir abaixo." 
                : "As hospedagens serão agrupadas por página (padrão)."}
            </div>
            
            {useCustomGroups && (
              <div className="mt-6 space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium">Grupos Personalizados</h4>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleAddGroup}
                    disabled={!isEditing}
                  >
                    <PlusCircle className="h-4 w-4 mr-2" /> 
                    Adicionar Grupo
                  </Button>
                </div>
                
                <Separator />
                
                <DragDropContext onDragEnd={handleDragEnd}>
                  {renderUnassignedAccommodations()}
                  
                  <ScrollArea className="h-[500px] pr-4">
                    <Droppable droppableId="groups" type="GROUP">
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className="space-y-4"
                        >
                          {sortedGroups.map((group, index) => (
                            <Draggable
                              key={group.id}
                              draggableId={group.id}
                              index={index}
                              isDragDisabled={!isEditing}
                            >
                              {(provided) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                >
                                  <Card>
                                    {editingGroupId === group.id ? (
                                      <>
                                        <CardHeader className="pb-2">
                                          <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                              {isEditing && (
                                                <div {...provided.dragHandleProps}>
                                                  <GripVertical className="h-5 w-5" />
                                                </div>
                                              )}
                                              <Input
                                                value={newGroupName}
                                                onChange={(e) => setNewGroupName(e.target.value)}
                                                className="h-8 w-[200px]"
                                              />
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleSaveGroupName(group.id)}
                                              >
                                                Salvar
                                              </Button>
                                              <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => setEditingGroupId(null)}
                                              >
                                                Cancelar
                                              </Button>
                                            </div>
                                          </div>
                                        </CardHeader>
                                        <CardContent>
                                          <div className="space-y-2">
                                            <label className="text-xs text-gray-500">Descrição do grupo:</label>
                                            <RichTextEditor
                                              value={newGroupDescription}
                                              onChange={setNewGroupDescription}
                                              placeholder="Descrição opcional para este grupo de hospedagens..."
                                              rows={3}
                                            />
                                          </div>
                                        </CardContent>
                                      </>
                                    ) : (
                                      <CardHeader className="pb-2">
                                        <div className="flex items-center justify-between">
                                          <div className="flex items-center gap-2">
                                            {isEditing && (
                                              <div {...provided.dragHandleProps}>
                                                <GripVertical className="h-5 w-5" />
                                              </div>
                                            )}
                                            <CardTitle className="text-sm">
                                              {group.name} ({group.accommodationIds.length} acomodações)
                                            </CardTitle>
                                          </div>
                                          
                                          {isEditing && (
                                            <div className="flex items-center space-x-1">
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7"
                                                onClick={() => handleEditGroupName(group.id, group.name, group.description)}
                                                title="Editar nome do grupo"
                                              >
                                                <Pencil className="h-4 w-4" />
                                              </Button>
                                              
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7"
                                                onClick={() => handleMoveGroup(group.id, 'up')}
                                                disabled={index === 0}
                                                title="Mover grupo para cima"
                                              >
                                                <ArrowUp className="h-4 w-4" />
                                              </Button>
                                              
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7"
                                                onClick={() => handleMoveGroup(group.id, 'down')}
                                                disabled={index === sortedGroups.length - 1}
                                                title="Mover grupo para baixo"
                                              >
                                                <ArrowDown className="h-4 w-4" />
                                              </Button>
                                              
                                              <Button
                                                variant="ghost"
                                                size="icon"
                                                className="h-7 w-7 text-destructive"
                                                onClick={() => handleRemoveGroup(group.id)}
                                                title="Remover grupo"
                                              >
                                                <Trash className="h-4 w-4" />
                                              </Button>
                                            </div>
                                          )}
                                        </div>
                                      </CardHeader>
                                    )}
                                    
                                    <CardContent>
                                      {group.description && group.description.trim() !== '' && (
                                        <div className="mb-4">
                                          <RichTextEditor
                                            value={group.description}
                                            onChange={() => {}}
                                            readOnly={true}
                                            className="border-none shadow-none bg-transparent p-0"
                                          />
                                        </div>
                                      )}
                                      <Droppable droppableId={group.id} type="ACCOMMODATION">
                                        {(provided) => (
                                          <div
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                            className="space-y-2 min-h-[50px]"
                                          >
                                            {group.accommodationIds.map((accId, index) => {
                                              const acc = getAccommodationById(accId);
                                              if (!acc) return null;
                                              
                                              return (
                                                <Draggable
                                                  key={accId}
                                                  draggableId={accId}
                                                  index={index}
                                                  isDragDisabled={!isEditing}
                                                >
                                                  {(provided) => (
                                                    <div
                                                      ref={provided.innerRef}
                                                      {...provided.draggableProps}
                                                      {...provided.dragHandleProps}
                                                      className="flex items-center justify-between bg-muted rounded-md p-2"
                                                    >
                                                      <div className="flex items-center gap-2">
                                                        {isEditing && <GripVertical className="h-4 w-4 text-gray-400 flex-shrink-0" />}
                                                        <span className="text-sm">
                                                          {acc.name} (Página {acc.page || 1})
                                                        </span>
                                                      </div>
                                                      
                                                      {isEditing && (
                                                        <Button
                                                          variant="ghost"
                                                          size="icon"
                                                          className="h-6 w-6"
                                                          onClick={() => handleRemoveAccommodationFromGroup(group.id, accId)}
                                                          title="Remover acomodação do grupo"
                                                        >
                                                          <X className="h-3 w-3" />
                                                        </Button>
                                                      )}
                                                    </div>
                                                  )}
                                                </Draggable>
                                              );
                                            })}
                                            {provided.placeholder}
                                          </div>
                                        )}
                                      </Droppable>
                                      
                                      {group.accommodationIds.length === 0 && (
                                        <div className="text-center py-2 text-sm text-muted-foreground">
                                          Nenhuma acomodação neste grupo. Arraste acomodações para cá.
                                        </div>
                                      )}
                                    </CardContent>
                                  </Card>
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </ScrollArea>
                </DragDropContext>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
} 